/**
 * @module UseOperationProgress
 * @purpose Poll `GET .../operations/:operationId/progress` while an operation is
 *   running, for the one shared progress facility (FR16/FR16a) any long-running
 *   background task (staged verify, container inventory) publishes to.
 * @depends usePollingRefetch (interval + in-flight-skip), services/operation-progress,
 *   services/api (ApiError)
 * @dependedOnBy components/common/operation-progress/OperationProgress
 *
 * @rules
 * - Performance: composes usePollingRefetch verbatim -- inherits its default
 *   interval and in-flight skip rather than re-implementing polling.
 * - State: local useState; the failure-streak counter lives HERE (not in
 *   usePollingRefetch, which swallows transient errors by design).
 * - Errors: `MAX_CONSECUTIVE_FAILURES` consecutive request failures stop the
 *   auto-poll and surface `error` with a `retry` action; a 404 is terminal
 *   (`unknownOperation`) and never retried -- a spinner must never persist
 *   indefinitely.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../services/api'
import { getOperationProgress } from '../services/operation-progress'
import type { OperationProgress } from '../types/evidence-containers'
import { usePollingRefetch } from './use-polling-refetch'

const MAX_CONSECUTIVE_FAILURES = 3
const TERMINAL_STATUSES: ReadonlySet<string> = new Set(['succeeded', 'failed'])

export interface UseOperationProgressResult {
  progress: OperationProgress | null
  loading: boolean
  error: string | null
  unknownOperation: boolean
  retry: () => void
}

/**
 * @behaviour Fetches immediately, then polls while the operation is running.
 *   Stops polling on a terminal status, on an unknown-operation 404, or after
 *   `MAX_CONSECUTIVE_FAILURES` transient failures (surfacing `retry`).
 * @errors Never throws -- failures are captured into `error`/`unknownOperation`.
 */
export function useOperationProgress(
  caseId: string,
  operationId: string
): UseOperationProgressResult {
  const [progress, setProgress] = useState<OperationProgress | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [unknownOperation, setUnknownOperation] = useState(false)
  const [failing, setFailing] = useState(false)
  const failureCountRef = useRef(0)

  const fetchProgress = useCallback(async () => {
    try {
      const result = await getOperationProgress(caseId, operationId)
      failureCountRef.current = 0
      setFailing(false)
      setError(null)
      setProgress(result)
    } catch (err) {
      if (err instanceof ApiError && err.isNotFound) {
        setUnknownOperation(true)
        setError(err.message)
        return
      }
      failureCountRef.current += 1
      if (failureCountRef.current >= MAX_CONSECUTIVE_FAILURES) {
        setFailing(true)
        setError(err instanceof Error ? err.message : 'Failed to load operation progress')
      }
    } finally {
      setLoading(false)
    }
  }, [caseId, operationId])

  useEffect(() => {
    void fetchProgress()
  }, [fetchProgress])

  const isTerminal = progress !== null && TERMINAL_STATUSES.has(progress.status)
  const pollingEnabled = !unknownOperation && !failing && !isTerminal
  usePollingRefetch(fetchProgress, pollingEnabled)

  const retry = useCallback(() => {
    failureCountRef.current = 0
    setFailing(false)
    setError(null)
    void fetchProgress()
  }, [fetchProgress])

  return { progress, loading, error, unknownOperation, retry }
}
